import { Card } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useBets } from "../../common/contexts/betContext";
import { useFixtures } from "../../common/contexts/fixturesContext";
import { getGroupTable } from "../../services/scoreService";
import { palette } from "../../styles/palette";
import { Bet } from "../../types/Bet";
import { GroupTableData } from "../../types/GroupTableData";
import { Match } from "../../types/Match";
import { getMatchBettingState } from "../../utils/betting";
import FixtureCard from "../components/FixtureTile/FixtureCard";
import FixtureTile from "../components/FixtureTile/FixtureTile";
import FixtureTileBetHeader from "../components/FixtureTile/FixtureTileBetHeader";
import FixtureTileBody from "../components/FixtureTile/FixtureTileBody";
import FixturesPlaceholder from "./FixturesPlaceholder";
import "./FixtureStyles.scss";
import GroupPanel from "./GroupPanel";
import GroupTable from "./GroupTable";
import GroupTableSkeleton from "./GroupTableSkeleton";


interface FixtureGroupProps {
  setCurrentModalMatch: React.Dispatch<React.SetStateAction<Match>>;
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isDesktop?: boolean;
}

const FixtureGroup: React.FC<FixtureGroupProps> = ({
  setCurrentModalMatch,
  setIsModalOpen,
  isDesktop,
}) => {
  const [currentGroup, setCurrentGroup] = useState("A");
  const { matches } = useFixtures();
  const { bets } = useBets();
  const [currentFixtures, setCurrentFixtures] = useState(
    matches.filter(
      (match) =>
        match.matchType.type === "Group" &&
        match.matchType.value === currentGroup // filter by ongoing
    )
  );
  const [currentTableData, setCurrentTableData] = useState<GroupTableData[]>(
    []
  );
  useEffect(() => {
    setCurrentFixtures(
      matches.filter(
        (match) =>
          match.matchType.type === "Group" &&
          match.matchType.value === currentGroup &&
          !match.wasPlayed
      )
    );
    getGroupTable(currentGroup).then((data) => setCurrentTableData(data));
  }, [currentGroup, matches]);

  const selector = (
    <GroupPanel currentGroup={currentGroup} setCurrentGroup={setCurrentGroup} />
  );

  // Always render the compact table: on mobile the desktop column template
  // crushes the team-name column, and in the desktop sidebar the compact sizing
  // fits the narrower column without overflowing.
  const standings =
    currentTableData.length === 0 ? (
      <GroupTableSkeleton />
    ) : (
      <GroupTable groupTableData={currentTableData} />
    );

  const games = (
    <>
      {currentFixtures.length === 0 ? (
        <FixturesPlaceholder
          message={`All matches in Group ${currentGroup} have been played.`}
        />
      ) : (
        currentFixtures.map((match, index) => (
          <FixtureTile
            match={match}
            bet={bets.filter((bet) => bet.matchId === match.id)}
            setCurrentModalMatch={setCurrentModalMatch}
            setIsOpen={setIsModalOpen}
            isDesktop={isDesktop}
            key={`key-${match.associationId}-group-${index}`}
          />
        ))
      )}
    </>
  );

  if (isDesktop) {
    return (
      <div className="fixtureGroupD">
        <div className="fixtureGroupD__layout">
          <aside className="fixtureGroupD__side">
            <div className="fixtureGroupD__groupHeading">
              <span className="fixtureGroupD__groupKicker">Group</span>
              <span className="fixtureGroupD__groupLetter">{currentGroup}</span>
            </div>
            {selector}
            <div className="fixtureGroupD__tableWrap">{standings}</div>
          </aside>

          <section className="fixtureGroupD__games">
            <div className="fixtureGroupD__gamesHeader">
              <h3 className="fixtureGroupD__gamesTitle">Upcoming matches</h3>
              {currentFixtures.length > 0 && (
                <span className="fixtureGroupD__gamesCount">
                  {currentFixtures.length}
                </span>
              )}
            </div>
            <div className="fixtureGroupD__gamesList">{games}</div>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        gap: "20px",
        flexDirection: "column",
        alignItems: "center",
        paddingTop: "10px",
        width: "89%",
        height: "100%",
      }}
    >
      {selector}
      <div className="fixtureGroup__tableWrap">{standings}</div>
      {currentFixtures.length === 0 ? (
        <div
          style={{
            width: "100%",
            paddingBottom: "40px",
            display: "flex",
            justifyContent: "center",
          }}
        >
          {games}
        </div>
      ) : (
        <div style={{ width: "105%", paddingBottom: "40px" }}>{games}</div>
      )}
    </div>
  );
};

export default FixtureGroup;

export interface FixtureTileProps {
  showDate?: boolean;
  bets: Bet[];
  match: Match;
  setCurrentModalMatch: React.Dispatch<React.SetStateAction<Match>>;
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isDesktop?: boolean;
}

export const FixtureTileGroup: React.FC<FixtureTileProps> = ({
  showDate,
  bets,
  match,
  setCurrentModalMatch,
  setIsModalOpen,
  isDesktop,
}) => {
  const isBet = bets.length > 0;
  function handleClick() {
    setCurrentModalMatch(match);
    setIsModalOpen(true);
  }
  const isActive = getMatchBettingState(match) === "open";

  if (!isDesktop) {
    return <FixtureCard match={match} bet={bets} onClick={handleClick} />;
  }

  return (
    <Card
      style={{
        backgroundColor: palette.bgPrimary,
        width: "100%",
        height: "100%",
        maxHeight: "100px",
        borderRadius: "20px",
        marginTop: "20px",
        paddingBottom: isBet ? "30px" : undefined,
      }}
      className={isActive ? "hover-fixture-tile": undefined}
      onClick={isActive ? () => handleClick() : undefined}      
    >
      {isBet && (
        <FixtureTileBetHeader
          betAwayScore={bets[0].awayTeamScoreBet}
          betHomeScore={bets[0].homeTeamScoreBet}
          isDesktop={isDesktop}
        />
      )}
      <FixtureTileBody
        isBet={isBet}
        homeTeamName={match.homeTeam.internationalName}
        awayTeamName={match.awayTeam.internationalName}
        date={match.kickOffTime?.dateTime || ""}
        showDate={showDate}
        mBot="60px"
        isPlayed={match.wasPlayed}
        isDesktop={isDesktop}
        match={match}
      />
    </Card>
  );
};
